'use client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgePercent, faRightToBracket } from '@fortawesome/pro-duotone-svg-icons';
import Toggle from '@/components/common/Toggle';
import { useEffect, useState } from 'react';
import SubscriptionsTable from './comparison/SubscriptionsTable';
import { useRouter } from 'next/navigation';
import SubscriptionCardButton from './SubscriptionCardButton';
import Link from 'next/link';
import Button from '../common/Button';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { BillingPeriod } from '@/types/billingPeriod';
import { getSourcePath } from '@/rewrites';
import PaymentModal from '@/components/subscriptions/PaymentModal';
import { useAccount } from '@/hooks/useAccount';
import Cookies from 'js-cookie';
interface PriceComponentProps {
  showStripeActions?: boolean;
  hasPermission?: boolean;
  getPortalLink?: Function;
  loading?: boolean;
}

const PriceComponent: React.FC<PriceComponentProps> = ({
  showStripeActions = false,
  hasPermission,
  getPortalLink,
  loading,
}) => {
  const [isMonthly, setIsMonthly] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [referralCode, setReferralCode] = useState<string>();
  const { isAuthenticated } = useAuthenticated();
  const { customer, currentTier } = useAccount();
  const router = useRouter();

  useEffect(() => {
    setReferralCode(Cookies.get('bidpilot_referral_code'));
  }, []);

  const handleToggleClick = () => {
    setIsMonthly((prev) => !prev);
  };

  const handleChooseSubscriptionClick = async (tier: SubscriptionTier) => {
    if (
      customer &&
      customer.existingStripeAccount &&
      customer.existingStripeSubscription &&
      currentTier !== SubscriptionTier.BASIC
    ) {
      const portalLink = await getPortalLink?.();
      router.push(portalLink);
    } else {
      setSelectedTier(tier);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const renderCardButton = (tier: SubscriptionTier, isCurrent: boolean) => {
    return (
      <div className="flex h-14 w-full items-center justify-center bg-base-100 pt-4">
        <SubscriptionCardButton
          type={isCurrent ? 'current' : tier === SubscriptionTier.ENTERPRISE ? 'on request' : 'choose'}
          onClick={!isCurrent && hasPermission ? () => handleChooseSubscriptionClick(tier) : undefined}
          disabled={!hasPermission || isCurrent}
        />
      </div>
    );
  };

  const billingPeriod = isMonthly ? BillingPeriod.MONTH : BillingPeriod.YEAR;

  return (
    <>
      <div className="flex flex-col items-center md:flex-row md:justify-between">
        <div className="my-2 flex items-center">
          <FontAwesomeIcon icon={faBadgePercent} className="h-7 w-7 text-accent" />
          &nbsp;
          <span className="mr-2 text-xl font-normal max-md:text-lg">Jahresabo:</span>
          <span className="text-xl font-medium max-md:text-lg">2 Monate geschenkt</span>
        </div>
        <Toggle isActive={isMonthly} onClick={handleToggleClick} primaryOption="monatlich" secondaryOption="jährlich" />
      </div>

      {!showStripeActions && <SubscriptionsTable billingPeriod={billingPeriod} discountCode={referralCode} />}

      {showStripeActions && (loading || !customer) && (
        <div className="flex h-full w-full items-center justify-center">
          <span className="loading-xl loading loading-spinner"></span>
        </div>
      )}

      {showStripeActions && !loading && customer && (
        <div className="mb-8">
          <SubscriptionsTable
            billingPeriod={billingPeriod}
            renderCardButton={renderCardButton}
            discountCode={referralCode}
          />
        </div>
      )}

      {!isAuthenticated && (
        <div className="flex justify-center max-md:mt-2">
          <Link href={getSourcePath('/registration')} target="_blank">
            <Button
              label="Kostenlos registrieren"
              icon={faRightToBracket}
              variant="secondary"
              titleClassName="text-sm font-normal"
            />
          </Link>
        </div>
      )}

      <PaymentModal
        isOpen={isModalOpen}
        onClose={closeModal}
        subscriptionTier={selectedTier!}
        billingPeriod={billingPeriod}
        discountCode={referralCode}
      />
    </>
  );
};

export default PriceComponent;
